import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import Modal from './Modal';

interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** style the confirm button as destructive */
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Replaces window.confirm — `if (await confirm({...})) { … }`. */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return fn;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<(v: boolean) => void>();

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const close = (result: boolean) => {
    resolver.current?.(result);
    resolver.current = undefined;
    setOpts(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={!!opts}
        onClose={() => close(false)}
        title={opts?.title ?? 'Please confirm'}
        width={440}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => close(false)}>
              {opts?.cancelLabel ?? 'Cancel'}
            </button>
            <button
              type="button"
              className={opts?.danger ? 'btn btn-danger' : 'btn btn-primary'}
              onClick={() => close(true)}
              autoFocus
            >
              {opts?.confirmLabel ?? 'Confirm'}
            </button>
          </>
        }
      >
        <p className="text-[13px] text-fg leading-relaxed">{opts?.message}</p>
      </Modal>
    </ConfirmContext.Provider>
  );
}
